/**
 * 经销商端API工具类
 * 提供缓存、去重、重试等优化功能
 */

// 缓存管理
const cache = {
  data: new Map(),
  
  /**
   * 获取缓存数据
   * @param {string} key 缓存键
   * @param {number} maxAge 最大缓存时间（毫秒）
   * @returns {any|null} 缓存数据，如果不存在或过期则返回null
   */
  get: (key, maxAge = 300000) => { // 默认5分钟缓存
    // 定期清理过期缓存
    cache.cleanup();
    if (!cache.data.has(key)) return null;
    
    const { value, timestamp } = cache.data.get(key);
    const now = Date.now();
    
    // 检查是否过期
    if (now - timestamp > maxAge) {
      cache.data.delete(key);
      return null;
    }
    
    return value;
  },
  
  /**
   * 设置缓存数据
   * @param {string} key 缓存键
   * @param {any} value 缓存值
   */
  set: (key, value) => {
    cache.data.set(key, {
      value,
      timestamp: Date.now()
    });
  },
  
  /**
   * 清除缓存数据
   * @param {string} key 缓存键，如果不提供则清除所有缓存
   */
  clear: (key) => {
    if (key) {
      cache.data.delete(key);
    } else {
      cache.data.clear();
    }
  },
  
  /**
   * 按类型清除缓存
   * @param {string} type 缓存类型前缀
   */
  clearByType: (type) => {
    for (const key of cache.data.keys()) {
      if (key.startsWith(type)) {
        cache.data.delete(key);
      }
    }
  },
  
  /**
   * 清理过期缓存
   */
  cleanup: () => {
    const now = Date.now();
    const maxCacheSize = 1000; // 最大缓存项数
    
    // 清理过期项
    for (const [key, item] of cache.data.entries()) {
      if (now - item.timestamp > 300000) { // 5分钟过期
        cache.data.delete(key);
      }
    }
    
    // 如果缓存项太多，删除最老的项
    if (cache.data.size > maxCacheSize) {
      const entries = Array.from(cache.data.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toDelete = entries.slice(0, cache.data.size - maxCacheSize);
      toDelete.forEach(([key]) => cache.data.delete(key));
    }
  }
};

// 并发请求控制
const pendingRequests = new Map();

const clearPendingByType = (type) => {
  if (!type) return;
  for (const key of pendingRequests.keys()) {
    if (key.startsWith(type)) {
      pendingRequests.delete(key);
    }
  }
};

const clearAllPending = () => {
  pendingRequests.clear();
};

/**
 * 生成安全的缓存键
 * @param {string} type 缓存类型
 * @param {Object} params 参数对象
 * @returns {string} 缓存键
 */
const generateCacheKey = (type, params) => {
  if (!params || typeof params !== 'object') {
    return `${type}_empty`;
  }
  
  // 排序参数键并过滤空值
  const sortedParams = Object.keys(params)
    .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
    .sort()
    .reduce((acc, key) => {
      acc[key] = params[key];
      return acc;
    }, {});
  
  // 使用简单的hash算法生成缓存键
  const paramString = JSON.stringify(sortedParams);
  let hash = 0;
  for (let i = 0; i < paramString.length; i++) {
    const char = paramString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 转为32bit整数
  }
  
  return `${type}_${Math.abs(hash).toString(36)}`;
};

/**
 * 处理API请求
 * @param {Function} apiCall API调用函数
 * @param {Object} options 选项
 * @param {string} options.cacheKey 缓存键
 * @param {number} options.cacheDuration 缓存时间（毫秒）
 * @param {boolean} options.useCache 是否使用缓存
 * @param {number} options.retries 重试次数
 * @param {number} options.retryDelay 重试延迟（毫秒）
 * @param {boolean} options.skipDuplicates 是否跳过重复请求
 * @param {Function} options.errorHandler 错误处理函数
 * @returns {Promise} API响应
 */
export const handleApiRequest = async (apiCall, options = {}) => {
  const {
    cacheKey,
    cacheDuration = 300000, // 默认5分钟
    useCache = true,
    retries = 2,
    retryDelay = 1000,
    skipDuplicates = true,
    errorHandler = null,
    cacheType = null // 新增缓存类型参数
  } = options;
  
  // 如果提供了cacheType和params，优先使用生成的安全缓存键
  let finalCacheKey = cacheKey;
  if (cacheType && options.params) {
    finalCacheKey = generateCacheKey(cacheType, options.params);
  }
  
  // 检查缓存
  if (useCache && finalCacheKey) {
    const cachedData = cache.get(finalCacheKey, cacheDuration);
    if (cachedData) {
      console.log(`[Dealer API] Using cached data for ${finalCacheKey}`);
      return Promise.resolve(cachedData);
    }
  }
  
  // 检查重复请求
  if (skipDuplicates && finalCacheKey && pendingRequests.has(finalCacheKey)) {
    console.log(`[Dealer API] Reusing pending request for ${finalCacheKey}`);
    return pendingRequests.get(finalCacheKey);
  }
  
  // 创建请求Promise
  const makeRequest = async (retriesLeft) => {
    try {
      console.log(`[Dealer API] Making request${retriesLeft < retries ? ` (retry ${retries - retriesLeft})` : ''}`);
      const response = await apiCall();
      
      // 更新缓存
      if (useCache && finalCacheKey) {
        cache.set(finalCacheKey, response);
      }
      
      return response;
    } catch (error) {
      console.error(`[Dealer API] Request failed:`, error);
      
      // 自定义错误处理
      if (errorHandler) {
        try {
          const handledResult = errorHandler(error);
          if (handledResult !== undefined) {
            return handledResult;
          }
        } catch (handlerError) {
          console.error('[Dealer API] Error handler failed:', handlerError);
        }
      }
      
      // 重试逻辑
      if (retriesLeft > 0 && shouldRetry(error)) {
        console.log(`[Dealer API] Retrying... (${retriesLeft} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return makeRequest(retriesLeft - 1);
      }
      
      // 增强错误信息
      enhanceError(error);
      throw error;
    }
  };
  
  // 执行请求
  const requestPromise = makeRequest(retries);
  
  // 添加到挂起请求
  if (skipDuplicates && finalCacheKey) {
    pendingRequests.set(finalCacheKey, requestPromise);
    
    // 请求完成后删除
    requestPromise.finally(() => {
      pendingRequests.delete(finalCacheKey);
    });
  }
  
  return requestPromise;
};

/**
 * 判断是否应该重试
 * @param {Error} error 错误对象
 * @returns {boolean} 是否应该重试
 */
const shouldRetry = (error) => {
  // 网络错误或5xx服务器错误可以重试
  if (!error.response) return true;
  
  const status = error.response.status;
  return status >= 500 || status === 408 || status === 429;
};

/**
 * 增强错误信息
 * @param {Error} error 错误对象
 */
const enhanceError = (error) => {
  if (error.response) {
    error.isApiError = true;
    error.statusCode = error.response.status;
    error.statusText = error.response.statusText;
    
    // 特定错误类型标记
    if (error.response.status === 401) {
      error.isUnauthorized = true;
    } else if (error.response.status === 403) {
      error.isForbidden = true;
    } else if (error.response.status === 404) {
      error.isNotFound = true;
    } else if (error.response.status >= 500) {
      error.isServerError = true;
    }
  } else if (error.request) {
    error.isNetworkError = true;
  }
};

/**
 * 清除指定类型的缓存
 * @param {string} type 缓存类型
 */
export const clearCacheByType = (type) => {
  cache.clearByType(type);
  // 变更数据后强制刷新：避免复用旧的 pending request 造成“看起来没生效”
  clearPendingByType(type);
};

/**
 * 清除所有缓存
 */
export const clearAllCache = () => {
  cache.clear();
  clearAllPending();
};

/**
 * 获取缓存统计信息
 * @returns {Object} 缓存统计
 */
export const getCacheStats = () => {
  return {
    size: cache.data.size,
    keys: Array.from(cache.data.keys())
  };
};

// 缓存键前缀常量
export const CACHE_KEYS = {
  ACCOUNTS: 'accounts_',
  STATS: 'stats_',
  REGIONS: 'regions_',
  SUBJECTS: 'subjects_',
  CARDS: 'cards_',
  COURSE_CONTENT: 'course_content_'
};
